// @flow
import 'isomorphic-fetch'
import * as actionCreators from './index'
import changeCase from 'change-object-case'
import { API, createDefaultHeaders } from '../../util/api'
import { isInvalid } from '@rachio-npm/com-rachio-web-core/util/validation'
import * as zoneSchema from './schemas'

/**
 * A module that exports `get`, and `update` functions for zones
 * @module
 */

const Api = new API()

/**
 * Get zone
 * @param {string} userAuthToken - User's authorization token
 * @param {string} zoneId - Zone id
 * @returns {object} An action related to the zone retrieval process
 */
export function get(userAuthToken: string, zoneId: string) {
  return function(dispatch: Function) {
    const reqObj = {
      method: 'GET',
      headers: {
        ...createDefaultHeaders(userAuthToken),
        'Content-Type': 'application/json'
      }
    }

    dispatch(actionCreators.zoneGetInit({ zoneId }))

    return Api.call(`zone/getZoneDetail/${zoneId}`, reqObj)
      .then(response =>
        response
          .json()
          .then(json =>
            dispatch(actionCreators.zoneGetComplete(json.zoneSummary))
          )
      )
      .catch(err => dispatch(actionCreators.zoneGetComplete(err)))
  }
}

/**
 * Update zone
 * @param {string} userAuthToken - User's authorization token
 * @param {Object} zoneData - Update data for request
 * @param {booean} advanced - Whether or not `zoneData` includes advanced zone settings
 * @returns {object} An action related to the zone update process
 */
export function update(
  userAuthToken: string,
  zoneData: Object,
  advanced: boolean
) {
  return function(dispatch: Function): Promise<*> {
    const reqData = changeCase.snakeKeys(zoneData, { recursive: true })
    const error = advanced
      ? isInvalid(zoneSchema.UpdateAdvancedZoneSchema, reqData)
      : isInvalid(zoneSchema.UpdateBasicZoneSchema, reqData)

    const reqObj = {
      method: 'PUT',
      body: JSON.stringify(reqData),
      headers: {
        ...createDefaultHeaders(userAuthToken),
        'Content-Type': 'application/json'
      }
    }
    dispatch(actionCreators.zoneUpdateInit({ zoneId: zoneData.zoneId }))

    if (error) {
      return Promise.reject(dispatch(actionCreators.zoneUpdateComplete(error)))
    }

    const callType = advanced ? 'updateAdvancedZone' : 'updateBasicZone'
    return Api.call(`zone/${callType}`, reqObj)
      .then(response =>
        response
          .json()
          .then(json => dispatch(actionCreators.zoneUpdateComplete(json)))
      )
      .catch(err => dispatch(actionCreators.zoneUpdateComplete(err)))
  }
}
